import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const STORAGE_KEY_ANALYTICS_BOT = "site2_analytics_bot";
const DEFAULT_ANALYTICS_BOT = "avrora";
const BOT_LABELS = {
  avrora: "Аврора (Telegram)",
  sverhum_vk: "Сверхум (VK)",
  sverhum_tg: "Сверхум (Telegram)"
};

const EMPTY_FUNNEL_PERIOD = {
  start: null,
  end: null,
  newUsers: 0,
  newUsersPrev: 0,
  newUsersDeltaPct: 0,
  joined: 0,
  joinRate: 0,
  activation: {
    activated: 0,
    rate: 0,
    fromJoinedRate: 0,
    timeAvgMin: 0,
    timeMedianMin: 0,
    p50: 0,
    p75: 0,
    p90: 0
  },
  aha: {
    users: 0,
    rateFromActivated: 0,
    avgRequestsDay1: 0,
    noAhaRate: 0
  },
  paywall: {
    viewUsers: 0,
    rateFromAha: 0,
    avgViewsBeforePurchase: 0,
    medianViewsBeforePurchase: 0,
    dist: { one: 0, twoThree: 0, fourPlus: 0 }
  },
  purchase: {
    users: 0,
    usersWithAha: 0,
    usersWithoutAha: 0,
    events: 0,
    crPaywallPurchase: 0,
    crAhaPurchase: 0,
    ahaLag: { samples: 0, avgDays: 0, medianDays: 0, day0: 0, day1: 0, day3: 0, day7: 0 }
  }
};

const EMPTY_MONEY_PERIOD = {
  revenue: 0,
  purchases: 0,
  purchaseUsers: 0,
  arppu: 0,
  tokens: 0,
  cost: 0,
  margin: 0,
  bySource: { vkdonut: 0, yookassa: 0, other: 0 },
  byPlan: {
    lite: { count: 0, revenue: 0 },
    pro: { count: 0, revenue: 0 },
    max: { count: 0, revenue: 0 }
  },
  renewals: 0
};

const EMPTY_ANALYTICS = {
  generatedAt: null,
  updatedAt: null,
  refreshing: false,
  stale: false,
  error: null,
  periods: {
    day: { ...EMPTY_FUNNEL_PERIOD },
    week: { ...EMPTY_FUNNEL_PERIOD },
    month: { ...EMPTY_FUNNEL_PERIOD }
  },
  dropOff: { steps: [], bottleneck: null },
  money: {
    day: { ...EMPTY_MONEY_PERIOD },
    week: { ...EMPTY_MONEY_PERIOD },
    month: { ...EMPTY_MONEY_PERIOD },
    activePaid: 0,
    activeByPlan: { lite: 0, pro: 0, max: 0 },
    newPayersMonth: 0,
    avgTimeToFirstPurchaseDays: 0,
    repeatPurchaseCount: 0,
    repeatPurchaseRate: 0,
    renewRate: 0,
    revenueFromOldUsersShare: 0,
    lifecycleDays: 0,
    avgMonths: 0,
    ltv: 0,
    cac: 0,
    ltvToCac: 0,
    avgWeeklyNewPayers: 0,
    goalWeeks: 0
  },
  retention: { d1: 0, d7: 0, d30: 0 },
  cohorts: { rows: [], insights: [] },
  executiveSummary: [],
  conclusions: { bottleneck: null, growthMetric: null, focus: null, goalWeeks: 0 },
  dashboard: { date: "", text: "" }
};

function MetricLabel({ title, hint }) {
  return (
    <div className="metric-label">
      <div className="metric-title">{title}</div>
      {hint ? <div className="metric-hint">{hint}</div> : null}
    </div>
  );
}

function MetricKpi({ title, value, hint }) {
  return (
    <div className="metric-kpi">
      <div className="metric-title">{title}</div>
      <div className="metric-value">{value}</div>
      {hint ? <div className="metric-hint">{hint}</div> : null}
    </div>
  );
}

function formatNumber(value) {
  const num = Number(value || 0);
  return Number.isFinite(num) ? num.toLocaleString("ru-RU") : "0";
}

function formatMoney(value) {
  return `${formatNumber(value)} ₽`;
}

function formatPercent(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "0%";
  if (num !== 0 && Math.abs(num) < 0.1) return `${num < 0 ? "-" : ""}<0.1%`;
  const rounded = Math.round(num * 10) / 10;
  return `${rounded}%`;
}

function formatSignedPercent(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "0%";
  const sign = num >= 0 ? "+" : "";
  if (num !== 0 && Math.abs(num) < 0.1) return `${sign}<0.1%`;
  const rounded = Math.round(num * 10) / 10;
  return `${sign}${rounded}%`;
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("ru-RU");
}

function formatShare(value, total) {
  const denom = Number(total || 0);
  if (!denom) return "0%";
  return formatPercent((Number(value || 0) / denom) * 100);
}

function formatAhaLag(purchase) {
  const samples = Number(purchase?.ahaLag?.samples || 0);
  if (samples <= 0) return "- / -";
  return `${formatNumber(purchase?.ahaLag?.avgDays)} / ${formatNumber(purchase?.ahaLag?.medianDays)}`;
}

function formatAhaLagBuckets(purchase) {
  const samples = Number(purchase?.ahaLag?.samples || 0);
  if (samples <= 0) return "- / - / - / -";
  return `${formatPercent(purchase?.ahaLag?.day0)} / ${formatPercent(purchase?.ahaLag?.day1)} / ${formatPercent(purchase?.ahaLag?.day3)} / ${formatPercent(purchase?.ahaLag?.day7)}`;
}

function normalizeAnalyticsPayload(payload) {
  const periods = payload?.periods || {};
  const money = payload?.money || {};
  const cohorts = payload?.cohorts || {};
  const retention = payload?.retention || {};
  const dropOff = payload?.dropOff || {};
  const conclusions = payload?.conclusions || {};
  const dashboard = payload?.dashboard || {};

  return {
    ...EMPTY_ANALYTICS,
    ...(payload || {}),
    periods: {
      day: { ...EMPTY_FUNNEL_PERIOD, ...(periods.day || {}) },
      week: { ...EMPTY_FUNNEL_PERIOD, ...(periods.week || {}) },
      month: { ...EMPTY_FUNNEL_PERIOD, ...(periods.month || {}) }
    },
    money: {
      ...EMPTY_ANALYTICS.money,
      ...money,
      day: { ...EMPTY_MONEY_PERIOD, ...(money.day || {}) },
      week: { ...EMPTY_MONEY_PERIOD, ...(money.week || {}) },
      month: { ...EMPTY_MONEY_PERIOD, ...(money.month || {}) }
    },
    cohorts: { ...EMPTY_ANALYTICS.cohorts, ...cohorts },
    retention: { ...EMPTY_ANALYTICS.retention, ...retention },
    dropOff: { ...EMPTY_ANALYTICS.dropOff, ...dropOff },
    conclusions: { ...EMPTY_ANALYTICS.conclusions, ...conclusions },
    dashboard: { ...EMPTY_ANALYTICS.dashboard, ...dashboard }
  };
}

export default function AdvancedAnalytics({ adminToken, botUrls = {}, botPresets = {} }) {
  const [analyticsData, setAnalyticsData] = useState({ ...EMPTY_ANALYTICS });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(true);
  const [selectedBot, setSelectedBot] = useState(
    localStorage.getItem(STORAGE_KEY_ANALYTICS_BOT) || DEFAULT_ANALYTICS_BOT
  );

  const availableBots = useMemo(() => {
    const keysFromUrls = Object.keys(botUrls || {});
    if (keysFromUrls.length) return keysFromUrls;
    const keysFromPresets = Object.keys(botPresets || {});
    if (keysFromPresets.length) return keysFromPresets;
    return Object.keys(BOT_LABELS);
  }, [botPresets, botUrls]);

  useEffect(() => {
    if (!availableBots.length) return;
    if (!availableBots.includes(selectedBot)) {
      setSelectedBot(availableBots[0]);
    }
  }, [availableBots, selectedBot]);

  useEffect(() => {
    if (selectedBot) {
      localStorage.setItem(STORAGE_KEY_ANALYTICS_BOT, selectedBot);
    }
  }, [selectedBot]);

  const analyticsApiUrl = String(botUrls?.[selectedBot] || "").trim().replace(/\/+$/, "");
  const selectedBotTitle = botPresets?.[selectedBot]?.title || BOT_LABELS[selectedBot] || selectedBot;

  const periodDay = analyticsData?.periods?.day || EMPTY_FUNNEL_PERIOD;
  const periodWeek = analyticsData?.periods?.week || EMPTY_FUNNEL_PERIOD;
  const periodMonth = analyticsData?.periods?.month || EMPTY_FUNNEL_PERIOD;
  const moneyDay = analyticsData?.money?.day || EMPTY_MONEY_PERIOD;
  const moneyWeek = analyticsData?.money?.week || EMPTY_MONEY_PERIOD;
  const moneyMonth = analyticsData?.money?.month || EMPTY_MONEY_PERIOD;
  const activeByPlan = analyticsData?.money?.activeByPlan || EMPTY_ANALYTICS.money.activeByPlan;
  const retention = analyticsData?.retention || EMPTY_ANALYTICS.retention;
  const dropOff = analyticsData?.dropOff || EMPTY_ANALYTICS.dropOff;
  const cohorts = analyticsData?.cohorts || EMPTY_ANALYTICS.cohorts;
  const conclusions = analyticsData?.conclusions || EMPTY_ANALYTICS.conclusions;
  const dashboard = analyticsData?.dashboard || EMPTY_ANALYTICS.dashboard;

  const analyticsRows = useMemo(
    () => [
      {
        key: "newUsers",
        label: <MetricLabel title="Новые регистрации" hint="Пользователи с regDate в выбранном периоде." />,
        day: `${formatNumber(periodDay.newUsers)} (${formatSignedPercent(periodDay.newUsersDeltaPct)})`,
        week: `${formatNumber(periodWeek.newUsers)} (${formatSignedPercent(periodWeek.newUsersDeltaPct)})`,
        month: `${formatNumber(periodMonth.newUsers)} (${formatSignedPercent(periodMonth.newUsersDeltaPct)})`
      },
      {
        key: "joined",
        label: <MetricLabel title="Дошли до входа в продукт" hint="Шаг после регистрации." />,
        day: `${formatNumber(periodDay.joined)} (${formatPercent(periodDay.joinRate)})`,
        week: `${formatNumber(periodWeek.joined)} (${formatPercent(periodWeek.joinRate)})`,
        month: `${formatNumber(periodMonth.joined)} (${formatPercent(periodMonth.joinRate)})`
      },
      {
        key: "activated",
        label: <MetricLabel title="Активация (1+ действие)" hint="Пользователь сделал первое полезное действие." />,
        day: `${formatNumber(periodDay.activation.activated)} (${formatPercent(periodDay.activation.rate)})`,
        week: `${formatNumber(periodWeek.activation.activated)} (${formatPercent(periodWeek.activation.rate)})`,
        month: `${formatNumber(periodMonth.activation.activated)} (${formatPercent(periodMonth.activation.rate)})`
      },
      {
        key: "fromJoined",
        label: <MetricLabel title="CR вход -> активация" hint="Доля активаций от шага входа." />,
        day: formatPercent(periodDay.activation.fromJoinedRate),
        week: formatPercent(periodWeek.activation.fromJoinedRate),
        month: formatPercent(periodMonth.activation.fromJoinedRate)
      },
      {
        key: "actAvg",
        label: <MetricLabel title="Время до активации (среднее, мин)" hint="Среднее время от регистрации до первого действия." />,
        day: formatNumber(periodDay.activation.timeAvgMin),
        week: formatNumber(periodWeek.activation.timeAvgMin),
        month: formatNumber(periodMonth.activation.timeAvgMin)
      },
      {
        key: "actMedian",
        label: <MetricLabel title="Время до активации (медиана, мин)" hint="Медианное время от регистрации до первого действия." />,
        day: formatNumber(periodDay.activation.timeMedianMin),
        week: formatNumber(periodWeek.activation.timeMedianMin),
        month: formatNumber(periodMonth.activation.timeMedianMin)
      },
      {
        key: "actP",
        label: <MetricLabel title="P50 / P75 / P90 активации" hint="Распределение времени активации." />,
        day: `${formatNumber(periodDay.activation.p50)} / ${formatNumber(periodDay.activation.p75)} / ${formatNumber(periodDay.activation.p90)}`,
        week: `${formatNumber(periodWeek.activation.p50)} / ${formatNumber(periodWeek.activation.p75)} / ${formatNumber(periodWeek.activation.p90)}`,
        month: `${formatNumber(periodMonth.activation.p50)} / ${formatNumber(periodMonth.activation.p75)} / ${formatNumber(periodMonth.activation.p90)}`
      },
      {
        key: "aha",
        label: <MetricLabel title="Aha-пользователи" hint="Пользователи, дошедшие до целевого поведения." />,
        day: `${formatNumber(periodDay.aha.users)} (${formatPercent(periodDay.aha.rateFromActivated)})`,
        week: `${formatNumber(periodWeek.aha.users)} (${formatPercent(periodWeek.aha.rateFromActivated)})`,
        month: `${formatNumber(periodMonth.aha.users)} (${formatPercent(periodMonth.aha.rateFromActivated)})`
      },
      {
        key: "reqDay1",
        label: <MetricLabel title="Действий в 1-й день (среднее)" hint="Среднее число ключевых действий в первые 24 часа." />,
        day: formatNumber(periodDay.aha.avgRequestsDay1),
        week: formatNumber(periodWeek.aha.avgRequestsDay1),
        month: formatNumber(periodMonth.aha.avgRequestsDay1)
      },
      {
        key: "noAha",
        label: <MetricLabel title="Доля без Aha" hint="Пользователи, не дошедшие до целевого поведения." />,
        day: formatPercent(periodDay.aha.noAhaRate),
        week: formatPercent(periodWeek.aha.noAhaRate),
        month: formatPercent(periodMonth.aha.noAhaRate)
      },
      {
        key: "paywallView",
        label: <MetricLabel title="Видели paywall" hint="Пользователи, которые увидели предложение оплаты." />,
        day: `${formatNumber(periodDay.paywall.viewUsers)} (${formatPercent(periodDay.paywall.rateFromAha)})`,
        week: `${formatNumber(periodWeek.paywall.viewUsers)} (${formatPercent(periodWeek.paywall.rateFromAha)})`,
        month: `${formatNumber(periodMonth.paywall.viewUsers)} (${formatPercent(periodMonth.paywall.rateFromAha)})`
      },
      {
        key: "paywallViews",
        label: <MetricLabel title="Просмотры до покупки (ср./мед.)" hint="Сколько раз пользователь видел paywall до первой покупки." />,
        day: `${formatNumber(periodDay.paywall.avgViewsBeforePurchase)} / ${formatNumber(periodDay.paywall.medianViewsBeforePurchase)}`,
        week: `${formatNumber(periodWeek.paywall.avgViewsBeforePurchase)} / ${formatNumber(periodWeek.paywall.medianViewsBeforePurchase)}`,
        month: `${formatNumber(periodMonth.paywall.avgViewsBeforePurchase)} / ${formatNumber(periodMonth.paywall.medianViewsBeforePurchase)}`
      },
      {
        key: "paywallDist",
        label: <MetricLabel title="Распределение просмотров paywall" hint="1 / 2-3 / 4+ просмотров до покупки." />,
        day: `${formatNumber(periodDay.paywall.dist.one)} / ${formatNumber(periodDay.paywall.dist.twoThree)} / ${formatNumber(periodDay.paywall.dist.fourPlus)}`,
        week: `${formatNumber(periodWeek.paywall.dist.one)} / ${formatNumber(periodWeek.paywall.dist.twoThree)} / ${formatNumber(periodWeek.paywall.dist.fourPlus)}`,
        month: `${formatNumber(periodMonth.paywall.dist.one)} / ${formatNumber(periodMonth.paywall.dist.twoThree)} / ${formatNumber(periodMonth.paywall.dist.fourPlus)}`
      },
      {
        key: "buyers",
        label: <MetricLabel title="Уникальные покупатели" hint="Пользователи с первой покупкой в периоде." />,
        day: `${formatNumber(periodDay.purchase.users)} (${formatPercent(periodDay.purchase.crPaywallPurchase)})`,
        week: `${formatNumber(periodWeek.purchase.users)} (${formatPercent(periodWeek.purchase.crPaywallPurchase)})`,
        month: `${formatNumber(periodMonth.purchase.users)} (${formatPercent(periodMonth.purchase.crPaywallPurchase)})`
      },
      {
        key: "ahaCr",
        label: <MetricLabel title="CR Aha -> Покупка" hint="Конверсия Aha-пользователей в покупку." />,
        day: formatPercent(periodDay.purchase.crAhaPurchase),
        week: formatPercent(periodWeek.purchase.crAhaPurchase),
        month: formatPercent(periodMonth.purchase.crAhaPurchase)
      },
      {
        key: "buyersAha",
        label: <MetricLabel title="Покупатели с Aha / без Aha" hint="Разделение покупателей по Aha-этапу." />,
        day: `${formatNumber(periodDay.purchase.usersWithAha)} / ${formatNumber(periodDay.purchase.usersWithoutAha)}`,
        week: `${formatNumber(periodWeek.purchase.usersWithAha)} / ${formatNumber(periodWeek.purchase.usersWithoutAha)}`,
        month: `${formatNumber(periodMonth.purchase.usersWithAha)} / ${formatNumber(periodMonth.purchase.usersWithoutAha)}`
      },
      {
        key: "ahaLag",
        label: <MetricLabel title="Лаг Aha -> Покупка (ср./мед., дни)" hint="Сколько времени проходит от Aha до первой покупки." />,
        day: formatAhaLag(periodDay.purchase),
        week: formatAhaLag(periodWeek.purchase),
        month: formatAhaLag(periodMonth.purchase)
      },
      {
        key: "ahaLagBuckets",
        label: <MetricLabel title="% покупок на 0 / 1 / 3 / 7 день" hint="Накопленные бакеты лага между Aha и покупкой." />,
        day: formatAhaLagBuckets(periodDay.purchase),
        week: formatAhaLagBuckets(periodWeek.purchase),
        month: formatAhaLagBuckets(periodMonth.purchase)
      }
    ],
    [periodDay, periodWeek, periodMonth]
  );

  async function fetchAnalytics(force = false) {
    if (!adminToken) return;

    setAnalyticsLoading(true);
    try {
      if (!analyticsApiUrl) {
        throw new Error("Не задан URL для аналитики");
      }

      const { data } = await axios.post(
        analyticsApiUrl,
        { method: "analytics", force, adminToken },
        { timeout: 35000 }
      );

      setAnalyticsData(normalizeAnalyticsPayload(data));
    } catch (error) {
      setAnalyticsData((prev) => ({
        ...prev,
        error: { message: error?.response?.data?.error || error.message || "Ошибка запроса аналитики" }
      }));
    } finally {
      setAnalyticsLoading(false);
    }
  }

  useEffect(() => {
    if (adminToken && analyticsApiUrl) {
      fetchAnalytics(false);
    }
  }, [adminToken, analyticsApiUrl, selectedBot]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!adminToken) return null;

  return (
    <section className="panel analytics-section-site2">
      <div className="section-header">
        <h2>Расширенная аналитика: {selectedBotTitle}</h2>
        <div className="section-header-actions">
          <select
            className="analytics-bot-select"
            value={selectedBot}
            onChange={(e) => setSelectedBot(e.target.value)}
          >
            {availableBots.map((botKey) => (
              <option key={botKey} value={botKey}>
                {botPresets?.[botKey]?.title || BOT_LABELS[botKey] || botKey}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="analytics-refresh"
            onClick={() => fetchAnalytics(true)}
            disabled={analyticsLoading}
          >
            {analyticsLoading ? "Обновление..." : "Обновить"}
          </button>
          <button type="button" className="section-toggle" onClick={() => setAnalyticsOpen((v) => !v)}>
            {analyticsOpen ? "Свернуть" : "Развернуть"}
          </button>
        </div>
      </div>

      {analyticsOpen && (
        <>
          <div className="analytics-meta">
            <span>Обновлено: {formatDateTime(analyticsData.updatedAt || analyticsData.generatedAt)}</span>
            <span>API: {analyticsApiUrl || "не задан"}</span>
            {analyticsData.refreshing ? <span className="analytics-chip">Обновляется...</span> : null}
            {analyticsData.stale ? <span className="analytics-chip warn">Данные устарели</span> : null}
          </div>

          {analyticsData.error ? (
            <div className="analytics-error">
              Ошибка аналитики: {analyticsData.error.message || "неизвестная ошибка"}
            </div>
          ) : null}

          <div className="analytics-block">
            <h3>Воронка продукта</h3>
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Метрика</th>
                  <th>День</th>
                  <th>Неделя</th>
                  <th>Месяц</th>
                </tr>
              </thead>
              <tbody>
                {analyticsRows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td>{row.day}</td>
                    <td>{row.week}</td>
                    <td>{row.month}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="analytics-block">
            <h3>Анализ потерь</h3>
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Шаг</th>
                  <th>Пользователи</th>
                  <th>% от предыдущего шага</th>
                  <th>Потери</th>
                </tr>
              </thead>
              <tbody>
                {dropOff.steps?.map((step, index) => (
                  <tr key={step.key || index}>
                    <td>{step.label}</td>
                    <td>{formatNumber(step.value)}</td>
                    <td>{index === 0 ? "-" : formatPercent(step.percentFromPrev)}</td>
                    <td>{index === 0 ? "-" : `${formatNumber(step.drop)} (${formatPercent(step.dropRate)})`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {dropOff.bottleneck ? (
              <div className="analytics-note">
                Основное узкое место: {dropOff.bottleneck.label} (-{formatNumber(dropOff.bottleneck.drop)}).
              </div>
            ) : null}
          </div>

          <div className="analytics-block">
            <h3>Деньги</h3>
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Метрика</th>
                  <th>День</th>
                  <th>Неделя</th>
                  <th>Месяц</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><MetricLabel title="Выручка" hint="Сумма всех покупок." /></td>
                  <td>{formatMoney(moneyDay.revenue)}</td>
                  <td>{formatMoney(moneyWeek.revenue)}</td>
                  <td>{formatMoney(moneyMonth.revenue)}</td>
                </tr>
                <tr>
                  <td><MetricLabel title="ARPPU" hint="Выручка / число платящих пользователей." /></td>
                  <td>{formatMoney(moneyDay.arppu)}</td>
                  <td>{formatMoney(moneyWeek.arppu)}</td>
                  <td>{formatMoney(moneyMonth.arppu)}</td>
                </tr>
                <tr>
                  <td><MetricLabel title="VK Donut" hint="Выручка из источника vkdonut." /></td>
                  <td>{formatMoney(moneyDay.bySource.vkdonut)}</td>
                  <td>{formatMoney(moneyWeek.bySource.vkdonut)}</td>
                  <td>{formatMoney(moneyMonth.bySource.vkdonut)}</td>
                </tr>
                <tr>
                  <td><MetricLabel title="YooKassa" hint="Выручка из источника yookassa." /></td>
                  <td>{formatMoney(moneyDay.bySource.yookassa)}</td>
                  <td>{formatMoney(moneyWeek.bySource.yookassa)}</td>
                  <td>{formatMoney(moneyMonth.bySource.yookassa)}</td>
                </tr>
                <tr>
                  <td><MetricLabel title="Повторные покупки" hint="Покупки продлений и повторов." /></td>
                  <td>{formatNumber(moneyDay.renewals)}</td>
                  <td>{formatNumber(moneyWeek.renewals)}</td>
                  <td>{formatNumber(moneyMonth.renewals)}</td>
                </tr>
                <tr>
                  <td><MetricLabel title="Маржинальность" hint="(Выручка - затраты) / выручка." /></td>
                  <td>{formatPercent(moneyDay.margin)}</td>
                  <td>{formatPercent(moneyWeek.margin)}</td>
                  <td>{formatPercent(moneyMonth.margin)}</td>
                </tr>
              </tbody>
            </table>

            <div className="analytics-kpis">
              <MetricKpi title="Новых плательщиков (месяц)" value={formatNumber(analyticsData.money.newPayersMonth)} />
              <MetricKpi title="Активных платных пользователей" value={formatNumber(analyticsData.money.activePaid)} />
              <MetricKpi title="До первой покупки (среднее)" value={`${formatNumber(analyticsData.money.avgTimeToFirstPurchaseDays)} дн`} />
              <MetricKpi title="Выручка от старых пользователей" value={formatPercent(analyticsData.money.revenueFromOldUsersShare)} />
            </div>

            <div className="analytics-kpis">
              <MetricKpi
                title="Старт"
                value={`${formatNumber(activeByPlan.lite || moneyMonth.byPlan.lite.count)} (${formatMoney(moneyMonth.byPlan.lite.revenue)})`}
              />
              <MetricKpi
                title="Мастер"
                value={`${formatNumber(activeByPlan.pro || moneyMonth.byPlan.pro.count)} (${formatMoney(moneyMonth.byPlan.pro.revenue)})`}
              />
              <MetricKpi
                title="Годовой"
                value={`${formatNumber(activeByPlan.max || moneyMonth.byPlan.max.count)} (${formatMoney(moneyMonth.byPlan.max.revenue)})`}
              />
            </div>

            <div className="analytics-kpis">
              <MetricKpi title="Доля старта" value={formatShare(moneyMonth.byPlan.lite.revenue, moneyMonth.revenue)} />
              <MetricKpi title="Доля мастера" value={formatShare(moneyMonth.byPlan.pro.revenue, moneyMonth.revenue)} />
              <MetricKpi title="Доля годового" value={formatShare(moneyMonth.byPlan.max.revenue, moneyMonth.revenue)} />
            </div>
          </div>

          <div className="analytics-block">
            <h3>Удержание и ценность</h3>
            <table className="analytics-table">
              <tbody>
                <tr>
                  <td><MetricLabel title="Удержание D1 / D7 / D30" hint="Возвраты пользователей по дням." /></td>
                  <td>{formatPercent(retention.d1)} / {formatPercent(retention.d7)} / {formatPercent(retention.d30)}</td>
                </tr>
                <tr>
                  <td><MetricLabel title="Повторные покупатели" hint="Пользователи с 2+ покупками." /></td>
                  <td>{formatNumber(analyticsData.money.repeatPurchaseCount)} ({formatPercent(analyticsData.money.repeatPurchaseRate)})</td>
                </tr>
                <tr>
                  <td><MetricLabel title="Доля продлений" hint="Доля продлений среди платящих." /></td>
                  <td>{formatPercent(analyticsData.money.renewRate)}</td>
                </tr>
                <tr>
                  <td><MetricLabel title="LTV" hint="Lifetime value пользователя." /></td>
                  <td>{formatMoney(analyticsData.money.ltv)}</td>
                </tr>
                <tr>
                  <td><MetricLabel title="CAC" hint="Стоимость привлечения (если считается)." /></td>
                  <td>{formatMoney(analyticsData.money.cac)}</td>
                </tr>
                <tr>
                  <td><MetricLabel title="LTV / CAC" hint="Соотношение дохода и стоимости привлечения." /></td>
                  <td>{formatNumber(analyticsData.money.ltvToCac)}</td>
                </tr>
                <tr>
                  <td><MetricLabel title="Жизненный цикл, дни" hint="Средняя длина платежного цикла." /></td>
                  <td>{formatNumber(analyticsData.money.lifecycleDays)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="analytics-block">
            <h3>Когорты</h3>
            {cohorts.rows?.length > 0 ? (
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Когорта</th>
                    <th>Пользователи</th>
                    <th>D1</th>
                    <th>D7</th>
                    <th>D30</th>
                    <th>Aha %</th>
                    <th>Покупка %</th>
                    <th>ARPPU</th>
                    <th>LTV</th>
                  </tr>
                </thead>
                <tbody>
                  {cohorts.rows.map((row) => (
                    <tr key={row.cohort}>
                      <td>{row.cohort}</td>
                      <td>{formatNumber(row.size)}</td>
                      <td>{formatPercent(row.retentionD1)}</td>
                      <td>{formatPercent(row.retentionD7)}</td>
                      <td>{formatPercent(row.retentionD30)}</td>
                      <td>{formatPercent(row.ahaRate)}</td>
                      <td>{formatPercent(row.purchaseRate)}</td>
                      <td>{formatMoney(row.arppu)}</td>
                      <td>{formatMoney(row.ltv)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="analytics-note">Данных по когортам пока нет.</div>
            )}
            {cohorts.insights?.length ? <div className="analytics-note">{cohorts.insights.join(" ")}</div> : null}
          </div>

          <div className="analytics-block">
            <h3>Итоги</h3>
            {analyticsData.executiveSummary?.length ? (
              <ul className="analytics-list">
                {analyticsData.executiveSummary.map((item, index) => (
                  <li key={`summary-${index}`}>{item}</li>
                ))}
              </ul>
            ) : (
              <div className="analytics-note">Итоги появятся после накопления данных.</div>
            )}
            <div className="analytics-kpis">
              <MetricKpi title="Главное узкое место" value={conclusions.bottleneck?.label || "-"} />
              <MetricKpi title="Ключевая метрика роста" value={conclusions.growthMetric || "-"} />
              <MetricKpi title="Фокус команды" value={conclusions.focus || "-"} />
              <MetricKpi title="Цель до 300 платных" value={conclusions.goalWeeks ? `${conclusions.goalWeeks} недель` : "-"} />
            </div>
          </div>

          <div className="analytics-block">
            <h3>Ежедневный дашборд</h3>
            <pre className="analytics-dashboard">{dashboard.text || "Нет данных"}</pre>
          </div>
        </>
      )}
    </section>
  );
}
